/**
 * The in-game "Report a bug" controls (`q-bug-report-button`). One click files a report; the
 * note is optional. The server attaches who, where, which run and which build — the player never
 * finds or copies an id. Two placements share one sender:
 *   - `BugReportForm` — Settings → Support: an optional note box and the button;
 *   - `BugReportHudButton` — the dungeon HUD while in a run: the button alone, one click.
 * After a run, the Settings button attaches that run too (the server falls back to the
 * character's latest finished run), so there is no separate outcome-screen button.
 */

import { useState } from 'react';

import { buildBugReportPayload } from '../net/bugReport.js';
import { buildSha } from '../version.js';
import { useLayoutMode } from './layout/useLayout.js';
import { useGame } from './net-context.js';

/** The shared sender: claims the one in-flight slot (a double-click files once), then sends. */
function useBugReportSender(): { send: (note?: string) => boolean; busy: boolean } {
  const { conn, store } = useGame();
  const layout = useLayoutMode();
  return {
    busy: store.isInFlight('bugReport', Date.now()),
    send: (note?: string) => {
      if (!store.beginRequest('bugReport', Date.now())) return false;
      conn.send(
        'bugReport',
        buildBugReportPayload({
          ...(note !== undefined ? { note } : {}),
          layout,
          clientBuild: buildSha(),
        }),
      );
      return true;
    },
  };
}

export function BugReportForm(): JSX.Element {
  const { send, busy } = useBugReportSender();
  const [note, setNote] = useState('');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="bug-report-form">
      <textarea
        className="pxl-input"
        rows={2}
        maxLength={500}
        placeholder="What happened? (optional)"
        aria-label="What happened? (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button
        type="button"
        className="pxl-btn"
        data-testid="bug-report-send"
        disabled={busy}
        onClick={() => {
          if (send(note)) setNote('');
        }}
      >
        Report a bug
      </button>
      <div className="pxl-faint">
        Sends where you are, your current or last dungeon run and the game version. No need to copy
        anything.
      </div>
    </div>
  );
}

export function BugReportHudButton(): JSX.Element {
  const { send, busy } = useBugReportSender();
  return (
    <button
      type="button"
      className="pxl-btn tiny interactive"
      data-testid="bug-report-hud"
      title="Report a bug in this run (one click)"
      disabled={busy}
      onClick={() => send()}
    >
      Report a bug
    </button>
  );
}
